import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { DashboardList } from './DashboardList';
import { READ, WRITE } from './Om7Sense';
import { ApiConnectionContext } from "./hooks/useApiConnectionContext";
import { TranslationContext } from "./hooks/useTranslationContext";
import {
    useDSSubscription,
} from "./hooks/useDSSubscription";

export const OK_STATES = ["RUNNING", "INITIALIZING", "REINITIALIZING", "STARTING"];

export const ALARM_STATES = ["LICENSE_INSUFFICIENT", "SOURCE_DISCONNECTED", "AUTHENTICATION_FAILED", "NOT_AVAILABLE", "MISCONFIGURED", "STOPPED"];

export const DISABLED_STATES = ["NOT_YET_STARTED", "DELETED"];

const doFilterByLocation = function(device, filter_location) {
    if (device.location !== undefined) {
        if (!filter_location || filter_location === "all") {
            return device;
        }
        if (filter_location === device.location) {
            return device;
        }
    }
};

const doFilterByType = function(device, filter_type) {
    if (device) {
        if (!filter_type || filter_type === "all") {
            return device;
        }
        if (device.type && filter_type === device.type) {
            return device;
        }
    }
};

const createSortByFunction = (sortOrder, sortBy) => function (first, second) {
    const byLocation = (sortBy === "location");
    const byName = (sortBy === "name");
    if (byLocation || byName) {
        const attribute = byLocation ? "location" : "name";
        let firstName = "";
        let secondName = "";
        if (first[attribute] !== undefined) {
            firstName = first[attribute];
        }
        if (second[attribute] !== undefined) {
            secondName = second[attribute];
        }

        if (!firstName || !secondName) {
            return 0;
        }

        if (sortOrder === "ascending") {
            return firstName.localeCompare(secondName);
        } else {
            return -firstName.localeCompare(secondName);
        }
    } else if (sortBy === "category") {
        if (!first.type || !second.type) {
            return 0;
        }
        if (sortOrder === "ascending") {
            return first.type.localeCompare(second.type);
        } else {
            return -first.type.localeCompare(second.type);
        }
    }
};

const getFilterFromChangeEvent = (event) => {
    const value = event.target.value;
    if (value === "__ALL__") {
        return null;
    }
    return value;
}

export const DashboardPage = ({ baseApiError }) => {
    const { p, loggedInAs } = useContext(ApiConnectionContext);
    const t = useContext(TranslationContext);
    const canCreateConnections = useMemo(() => p.checkPermission(WRITE, "connections"), [ p, loggedInAs ]);
    const sensor = useDSSubscription('sensor/+');
    const { v: filter_location  } = useDSSubscription("user/config/filter_device_location") || {};
    const { v: filter_type } = useDSSubscription("user/config/filter_device_type") || {};
    const { v: sort_order } = useDSSubscription("user/config/sort_device_order") || {};
    const { v: sort_by } = useDSSubscription("user/config/sort_device_attr") || {};

    const errorView = useMemo(() => {
        if (baseApiError) {
            const createHint = function(translationKey, goalKey, linkRoute) {
                const inputStr = t('details_status.' + translationKey);
                const goalPos = inputStr.indexOf("GOAL");
                if (goalPos === -1) {
                    return (
                        { inputStr }
                    );
                }
                const partOne = inputStr.substring(0, goalPos);
                return (
                    <span>
                        { partOne }
                        <Link to={ linkRoute }>{ t('details_status.' + goalKey) }</Link>
                        { inputStr.substr(goalPos + 4)}
                    </span>
                );
            };

            let errorMsg, errorHint = null;
            const errorStr = baseApiError.message ? baseApiError.message : baseApiError;
            errorMsg = t("error." + errorStr);
            if (errorStr === "no_license") {
                errorHint = createHint("device_hint_check_license", "device_hint_check_license_goal", "/settings/general");
            }

            return (
                <div className="overview">
                    <div className="detailpage__alert">
                        <div className="detailpage__alert__left">
                            <h2>{t('general.Error')}</h2>
                            <p>{ errorMsg }</p>
                        </div>
                        { errorHint ? (
                            <div className="detailpage__alert__right">
                                <h4>{t('details_status.next_steps')}</h4>
                                <p>{errorHint}</p>
                            </div>
                        ):null}
                    </div>
                </div>
            );
        }
    }, [ baseApiError, t ]);

    const sortFunction = useMemo(() => createSortByFunction(sort_order, sort_by), [ sort_order, sort_by ]);

    const { allTypes, allLocations, greenDevices, alarmDevices, warnedDevices } = useMemo(() => {
        const allTypes = new Set();
        const allLocations = new Set();
        // Ensure that what we are filtering by is always selectable
        if (filter_location) {
            allTypes.add(filter_location);
        }
        if (filter_type) {
            allLocations.add(filter_type);
        }
        const greenDevices = [], alarmDevices = [], warnedDevices = [];
        if (sensor) {
            for (let uuid in sensor) {
                if (sensor.hasOwnProperty(uuid) && uuid !== 'system') {
                    const device = sensor[uuid];

                    if (!device || !device.state) {
                        continue;
                    }

                    if (device.type) {
                        allTypes.add(device.type);
                    }
                    if (device.location) {
                        allLocations.add(device.location);
                    }
                    const filteredDevice = doFilterByType(doFilterByLocation(device, filter_location), filter_type);
                    if (filteredDevice) {
                        filteredDevice.uuid = uuid;
                        if (device.a && device.a.length > 0 || ALARM_STATES.indexOf(device.state) !== -1) {
                            alarmDevices.push(filteredDevice);
                        } else if (device.w && device.w.length > 0 || OK_STATES.indexOf(device.state) === -1) {
                            warnedDevices.push(filteredDevice);
                        } else {
                            greenDevices.push(filteredDevice);
                        }
                    }
                }
            }
            if (sort_by) {
                greenDevices.sort(sortFunction);
                warnedDevices.sort(sortFunction);
                alarmDevices.sort(sortFunction);
            }
        }
        return { allTypes, allLocations, greenDevices, alarmDevices, warnedDevices };
    }, [ sensor, filter_location, filter_type, sort_by, sortFunction ]);


    const allTypeOptions = useMemo(() => {
        const allTypeOptions = [
            (
                <option key="all_types" value="__ALL__">{ t('criteria.no_filter') }</option>
            )
        ];
        for (let type of allTypes) {
            const typeTrans = t('device_types.' + type.toLowerCase());
            allTypeOptions.push((
                <option value={ type } key={ type }>{ typeTrans }</option>
            ));
        }
        return allTypeOptions;
    }, [ allTypes, t ]);


    const allLocationOptions = useMemo(() => {
        const allLocationOptions = [
            (
                <option key="all_locations" value="__ALL__">{ t('criteria.no_filter') }</option>
            )
        ];
        for (let location of allLocations) {
            allLocationOptions.push((
                <option key={ location }>{location}</option>
            ));
        }
        return allLocationOptions;
    }, [ allLocations, t ]);


    // If we're showing a subpage make this page smallish
    const columnClass = 'col-12';

    if (errorView) {
        return errorView;
    }

    return (
        <React.Fragment>
            <div className="sidebar columns fixed_sidebar">
                <div className="sidebar__sorting active column col-6">
                    <div className="siderbar__sorting__container">
                        { t('criteria.orderby_by') }&nbsp;
                        <select onChange={function (event) { p.put("user/config/sort_device_attr", event.target.value, true); }} value={ sort_by }>
                            <option value="name">{ t('criteria.name') }</option>
                            <option value="category">{ t('criteria.category') }</option>
                            <option value="location">{ t('criteria.location') }</option>
                        </select>&nbsp;
                        <select onChange={function (event) { p.put("user/config/sort_device_order", event.target.value, true); }} value={ sort_order }>
                            <option value="ascending">{t('criteria.ascending')}</option>
                            <option value="descending">{t('criteria.descending')}</option>
                        </select>
                    </div>
                </div>
                <div className="sidebar__filter active column col-6 text-right">
                    <div className="siderbar__filter__container">
                        {t('criteria.filtering_by_type')}&nbsp;&nbsp;
                        <select onChange={ (event) => p.put("user/config/filter_device_type", getFilterFromChangeEvent(event), true) } value={ filter_type ? filter_type : "__ALL__" }>
                            { allTypeOptions }
                        </select>
                        &nbsp;{t('criteria.filtering_by_location')}&nbsp;
                        <select onChange={ (event) => p.put("user/config/filter_device_location", getFilterFromChangeEvent(event), true) } value={ filter_location ? filter_location : "__ALL__" }>
                            { allLocationOptions }
                        </select>
                    </div>
                </div>
            </div>
            <div id="deviceList" className="content columns col-gapless">
                <div className={"column details focus " + columnClass}>
                    <DashboardList otherDevices={ greenDevices } alarmDevices={ alarmDevices } warnedDevices={ warnedDevices }
                        canCreateConnections={ canCreateConnections } />
                </div>
            </div>
            <div className="overview__footer">
                <a href="#">{t('general.return_to_top')}</a>
            </div>
        </React.Fragment>
    );
}

